import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import config from "../../../config";

const InventoryValueContribution = () => {
    const token = localStorage.getItem("token");
    const branchId = localStorage.getItem("branch_id");

    const [segmentation, setSegmentation] = useState([]);
    const [topSkus, setTopSkus] = useState([]);
    const [totalValue, setTotalValue] = useState(0);
    const [activeIndex, setActiveIndex] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchInventoryValue = async () => {
            try {
                const res = await fetch(
                    `${config.apiUrl}/api/swalook/analytics/inventory/value/?branch_name=${branchId}`,
                    {
                        headers: {
                            Authorization: `Token ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!res.ok) throw new Error("API failed");

                const json = await res.json();
                const data = json.data || {};

                setSegmentation(data.segmentation || []);
                setTopSkus(data.topSkus || []);
                setTotalValue(Number(data.total_value) || 0);
            } catch (err) {
                console.error("Inventory value error:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchInventoryValue();
    }, [token, branchId]);

    const donutSeries = useMemo(
        () => segmentation.map((item) => Number(item.value) || 0),
        [segmentation]
    );

    const donutOptions = useMemo(
        () => ({
            chart: {
                type: "donut",
                events: {
                    dataPointMouseEnter: (_, __, config) => {
                        setActiveIndex(config.dataPointIndex);
                    },
                    dataPointMouseLeave: () => {
                        setActiveIndex(null);
                    },
                },
            },
            labels: segmentation.map((item) => item.name),
            colors: ["#2563EB", "#22C55E", "#F59E0B", "#EF4444"],
            legend: {
                position: "bottom",
                fontSize: "13px",
                markers: { radius: 12 },
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: "72%",
                        labels: {
                            show: false, // ❌ TURN OFF APEX CENTER LABELS
                        },
                    },
                },
            },
            dataLabels: { enabled: false },
            tooltip: {
                y: {
                    formatter: (val) => `₹${val.toLocaleString()}`,
                },
            },
        }),
        [segmentation]
    );

    const centerTitle =
        activeIndex === null
            ? "Total Inventory"
            : segmentation[activeIndex]?.name;

    const centerValue =
        activeIndex === null
            ? `₹${totalValue.toLocaleString()}`
            : `₹${Number(
                  segmentation[activeIndex]?.value || 0
              ).toLocaleString()}`;

    if (loading) {
        return (
            <div className="bg-white rounded-[2.5rem] shadow p-6 animate-pulse h-[320px]" />
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-[2.5rem] shadow p-6 text-red-600">
                Failed to load inventory value data
            </div>
        );
    }

    return (
        <div className="bg-white rounded-[2.5rem] shadow p-6">
            <div className="mb-5">
                <h2 className="text-lg font-semibold text-gray-800">
                    Inventory Value & Contribution
                </h2>
                <p className="text-sm text-gray-500">
                    Inventory value distribution by SKU
                </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Donut with custom center */}
                <div className="relative flex justify-center">
                    <Chart
                        options={donutOptions}
                        series={donutSeries}
                        type="donut"
                        width={320}
                    />

                    {/* 🔥 CUSTOM CENTER (React-controlled) */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <p className="text-sm text-gray-500">{centerTitle}</p>
                        <p className="text-xl font-bold text-gray-800">
                            {centerValue}
                        </p>
                    </div>
                </div>

                {/* Top SKUs */}
                <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                        Top High-Value SKUs
                    </h3>

                    {topSkus.map((sku) => (
                        <div
                            key={sku.id}
                            className="flex justify-between items-center bg-gray-50 rounded-xl px-4 py-3 mb-2"
                        >
                            <div>
                                <p className="text-sm font-medium text-gray-700">
                                    {sku.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                    Qty: {sku.qty}
                                </p>
                            </div>
                            <p className="font-semibold text-gray-800">
                                ₹{Number(sku.value).toLocaleString()}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default InventoryValueContribution;
